<template>
  <img
    ref="img"
    :src="INITIAL_SRC"
    :style="style"
    draggable="false"
    @dragstart="dragstart"
  />
</template>

<script>
export class ImageError extends Error {
  constructor(message = 'Failed to load image') {
    super() // calling super with message doesn't work
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ImageError)
    }
    this.message = message
    this.name = 'ImageError' // for Rollbar
  }
}

export default {
  name: 'PageImage',

  props: {
    src: {
      type: String,
      required: true,
    },
    width: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      INITIAL_SRC:
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=',
      loading: false,
    }
  },

  computed: {
    style() {
      return {
        position: 'absolute', // on Zoomable
        width: `${this.width}px`,
      }
    },
  },

  watch: {
    src() {
      this.setSrc(this.src)
    },
  },

  mounted() {
    this.setSrc(this.src)
  },

  beforeDestroy() {
    // Remove the onerror (and onload, for completeness) handlers,
    // because we don't want a cancelled fetch to trigger our
    // error-handling. https://stackoverflow.com/a/13459106/347386
    this.$refs.img.onerror = null
    this.$refs.img.onload = null
  },

  methods: {
    setSrc(src) {
      const {img} = this.$refs

      if (this.loading) {
        // Avoid triggering img.onerror for a cancelled fetch by
        // clearing handlers and setting it back to the initial 1x1
        // transparent png before setting the new src.
        img.onerror = null
        img.onload = null
        img.src = this.INITIAL_SRC
      }

      // img.onload and img.onerror handlers must be set *before* the
      // src attribute.
      img.onload = () => {
        this.$nextTick(() => {
          // Firefox doesn't decode the image by default until it's on
          // the screen, but that causes a noticeable delay changing
          // pages. Force loaded images to decode immediately since
          // we're managing how many load at once.
          img.decode && img.decode().catch(() => {})
        })
        this.loading = false
        this.$emit('loaded', img)
      }
      img.onerror = () => {
        this.loading = false
        this.$emit('denied', new ImageError())
      }

      // Handlers are installed, now attempt loading.
      this.loading = true
      img.src = src.replace(/ /g, '%20') // HACK, should fix server
    },

    /**
     * Prevent dragging on Firefox, which doesn't always respect
     * draggable="false". Preventing dragging is important so the page
     * remains pannable with the mouse on desktop, rather than uselessly
     * starting a drag-n-drop operation on the image.
     *
     * https://stackoverflow.com/questions/26356877/html5-draggable-false-not-working-in-firefox-browser
     */
    dragstart(event) {
      event.preventDefault()
    },
  },
}
</script>
